<script setup>
import { ref, computed } from 'vue'
import StatusPill from '@/components/shell/StatusPill.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { STAGES, tatSince, formatTat } from '@/data/shared/orderModel.js'

const search = ref(''); const statusFilter = ref('All')
const drawerOrderNo = ref(null)
const cancelReason = ref('')

const orders = computed(() => store.pharmacyV2.orders.value)
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return orders.value.filter(o => {
    const matchesQ = !q || o.orderNo.toLowerCase().includes(q) || o.lineItems.some(li => li.productName.toLowerCase().includes(q))
    const matchesStatus = statusFilter.value === 'All' || o.currentStage === statusFilter.value
    return matchesQ && matchesStatus
  }).sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
})
const statusOptions = computed(() => ['All', ...new Set(orders.value.map(o => o.currentStage))])

const drawerOrder = computed(() => drawerOrderNo.value ? orders.value.find(o => o.orderNo === drawerOrderNo.value) || null : null)
const stageIdx = computed(() => drawerOrder.value ? STAGES.indexOf(drawerOrder.value.currentStage) : -1)
const isPending = computed(() => drawerOrder.value?.currentStage === 'Order Received')
const isLateral = computed(() => drawerOrder.value && !STAGES.includes(drawerOrder.value.currentStage))

function openDrawer(o) { drawerOrderNo.value = o.orderNo; cancelReason.value = '' }
function confirm() { if (drawerOrderNo.value) store.pharmacyV2.confirmOrder(drawerOrderNo.value) }
function cancel() {
  if (!drawerOrderNo.value) return
  store.pharmacyV2.cancelOrder(drawerOrderNo.value, cancelReason.value.trim())
  cancelReason.value = ''
}
</script>

<template>
  <div>
    <div class="card module-header"><h2>{{ orders.length }} orders</h2></div>

    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search order no. or product…" />
        <select class="filter-select" v-model="statusFilter"><option v-for="s in statusOptions" :key="s">{{ s }}</option></select>
        <span class="result-count">{{ filtered.length }} of {{ orders.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Order No.</th><th>Date</th><th>Products</th><th>Value</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="o in filtered" :key="o.orderNo" class="clickable" @click="openDrawer(o)">
              <td class="cell-strong">{{ o.orderNo }}</td>
              <td class="cell-muted">{{ o.orderDate }}</td>
              <td>{{ o.lineItems[0].productName }}<span v-if="o.lineItems.length>1" class="cell-muted"> +{{ o.lineItems.length-1 }} more</span></td>
              <td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td>
              <td><StatusPill :status="o.currentStage" :label="o.currentStage" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerOrder" :title="drawerOrder?.orderNo || ''" @close="drawerOrderNo=null">
      <template v-if="drawerOrder">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Order Date</div>{{ drawerOrder.orderDate }}</div>
          <div><div class="label">Total Value</div>${{ drawerOrder.orderValue.toLocaleString() }}</div>
          <div><div class="label">Status</div><StatusPill :status="drawerOrder.currentStage" :label="drawerOrder.currentStage" /></div>
        </div>

        <div class="eyebrow" style="margin-bottom:8px;">Products</div>
        <table class="data-table" style="margin-bottom:18px;">
          <thead><tr><th>Product</th><th>Ordered</th><th>Free</th><th>Total Delivered</th><th>Unit Price</th><th>Line Value</th></tr></thead>
          <tbody>
            <tr v-for="li in drawerOrder.lineItems" :key="li.productSku">
              <td class="cell-strong">{{ li.productName }} <span v-if="li.schemeApplied" class="tier-badge Gold">10+1</span></td>
              <td class="cell-muted">{{ li.quantity }}</td>
              <td class="cell-muted">{{ li.freeQuantity || 0 }}</td>
              <td class="cell-strong">{{ li.totalQuantity || li.quantity }}</td>
              <td class="cell-muted">${{ li.unitPrice }}</td>
              <td class="cell-muted">${{ li.lineValue.toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="!isLateral" class="eyebrow" style="margin-bottom:10px;">Status Timeline</div>
        <div v-if="!isLateral" class="stepper" style="margin-bottom:18px;">
          <div v-for="(s,i) in STAGES" :key="s" class="stepper-step">
            <div class="stepper-marker">
              <div class="stepper-dot" :class="{ done: i<stageIdx, current: i===stageIdx }"></div>
              <div v-if="i<STAGES.length-1" class="stepper-line" :class="{ done: i<stageIdx }"></div>
            </div>
            <div class="stepper-label" :class="i===stageIdx?'current':(i<stageIdx?'':'pending')">
              {{ s }}
              <div v-if="drawerOrder.statusHistory[i]" class="cell-muted" style="font-size:10.5px;">{{ new Date(drawerOrder.statusHistory[i].timestamp).toLocaleString() }}</div>
            </div>
          </div>
        </div>
        <div v-if="!isLateral && stageIdx < STAGES.length-1" class="cell-muted" style="font-size:12px;margin-bottom:18px;">
          In {{ drawerOrder.currentStage }}: {{ formatTat(tatSince(drawerOrder)) }}
        </div>

        <div v-if="drawerOrder.currentStage==='Cancelled'" class="oos-remarks" style="margin-bottom:18px;">
          <strong style="display:block;font-size:11px;text-transform:uppercase;color:var(--color-text-muted);margin-bottom:3px;">Cancelled</strong>
          {{ drawerOrder.cancelReason || '—' }}
        </div>

        <div v-if="drawerOrder.comments && drawerOrder.comments.length" style="margin-bottom:18px;">
          <div class="eyebrow" style="margin-bottom:8px;">Comments</div>
          <div v-for="(c,i) in drawerOrder.comments" :key="i" class="oos-remarks" style="margin-bottom:8px;">
            <strong style="display:block;font-size:11px;color:var(--color-text-muted);margin-bottom:2px;">{{ c.from }} — {{ new Date(c.timestamp).toLocaleString() }}</strong>
            {{ c.text }}
          </div>
        </div>

        <div v-if="isPending" style="border-top:1px solid var(--color-border);padding-top:16px;">
          <button class="btn-primary" style="margin-right:8px;" @click="confirm">Confirm</button>
          <div style="display:flex;gap:8px;margin-top:10px;">
            <input class="form-input" v-model="cancelReason" placeholder="Reason (optional)" />
            <button class="filter-select" style="cursor:pointer;color:var(--status-red);" @click="cancel">Cancel Order</button>
          </div>
        </div>
      </template>
    </DetailDrawer>
  </div>
</template>
